import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useClinicianClinics, useCreatePatient } from '@/hooks/queries';
import { useAuthStore } from '@/auth';
import { toast } from '@/components/ui/toaster';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { ClinicCombobox } from '@/components/ClinicCombobox';
import {
  PATIENT_TYPES,
  type CreatePatientInput,
} from '@/api/patients';
import { PatientFlagsSection } from './PatientFlagsSection';
import type { PatientFlagsState } from '@/lib/patientFlags';
import { addPatientCadences, type Cadence } from '@/lib/subscriptionCadence';

/**
 * Maps trial-day count to the canonical `subscription_name` enum value the
 * server expects. Mirrors v1's `SUBSCRIPTION_NAME_MAP`
 * (clinician/src/services/patient.ts:23).
 */
const TRIAL_DAYS_TO_NAME: Record<number, CreatePatientInput['subscription_name']> = {
  3: 'three-day',
  5: 'five-day',
  7: 'seven-day',
  10: 'ten-day',
  14: '14-day',
  21: '21-day',
  28: '28-day',
  30: 'one-month',
};

const RECURRING_DAYS: Partial<Record<Cadence, number>> = {
  weekly: 7,
  'bi-weekly': 14,
  '4-weeks': 28,
  monthly: 30,
};

/** Friendly labels for the Cadence select. v1 uses
 *  `getSubscriptionTypeOptions(t).find(...).labelTr`; we inline the same
 *  English strings so they go through t() in the JSX. */
const CADENCE_LABELS: Record<Cadence, string> = {
  single: 'Single trial',
  weekly: 'Weekly',
  'bi-weekly': 'Bi-weekly',
  '4-weeks': '4 weeks',
  monthly: 'Monthly',
  custom: 'Custom',
  none: 'No initial logging period',
};

/**
 * Fallback for `clinic_default_days_until_report_date` when the clinic's
 * resolved flags don't carry it. Matches the server's own default for that
 * flag (MNT `app/feature_flags/feature_flags.py`), so an unresolved flag
 * lands the report date exactly where the server would have put it.
 */
const DEFAULT_DAYS_UNTIL_REPORT = 60;

/** Initial end date offered for a custom period, before the clinician picks one. */
const CUSTOM_END_SEED_DAYS = 28;

const todayIso = (): string => new Date().toISOString().slice(0, 10);

const addDaysIso = (start: string, days: number): string => {
  const d = new Date(`${start}T00:00:00`);
  if (Number.isNaN(d.getTime())) return start;
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const addMonthsIso = (start: string, months: number): string => {
  const d = new Date(`${start}T00:00:00`);
  if (Number.isNaN(d.getTime())) return start;
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

/**
 * Generate the valid recurring end-date options for a cadence. Mirrors v1's
 * `disableDatesNotInMultiple` — only dates that fall on cadence-multiples
 * of `start`, capped at 6 months out.
 */
const recurringEndOptions = (start: string, cadence: Cadence): string[] => {
  if (!start) return [];
  const cap = addMonthsIso(start, 6);
  const out: string[] = [];
  if (cadence === 'monthly') {
    for (let i = 1; i <= 6; i++) out.push(addMonthsIso(start, i));
    return out;
  }
  const stepDays = cadence === 'weekly' ? 7 : cadence === 'bi-weekly' ? 14 : cadence === '4-weeks' ? 28 : 0;
  if (stepDays === 0) return [];
  for (let i = 1; ; i++) {
    const next = addDaysIso(start, stepDays * i);
    if (next > cap) break;
    out.push(next);
  }
  return out;
};

/**
 * Parse the clinician's `hospital_patient_custom_fields_json` flag into a
 * list of field definitions. Each entry is either a free-text input
 * (no `values` key) or a select (when `values` is present).
 *
 * Returns an empty list if the flag is missing or malformed — the form
 * just won't render the custom-fields section.
 */
type CustomFieldDef = {
  label: string;
  field: string;
  values?: Record<string, { label: string; value: string; description?: string }>;
};

const parseCustomFields = (raw: unknown): CustomFieldDef[] => {
  if (typeof raw !== 'string' || raw.trim() === '') return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((d) => d && typeof d === 'object' && d.label && d.field) as CustomFieldDef[];
  } catch {
    return [];
  }
};

// Field order matters: zod's `safeParse` reports issues in declaration
// order, and the dialog only surfaces the first issue via `setError(...)`.
// `clinic_id` sits first so a clinician who clicks "Add" with nothing
// filled in sees "Choose a clinic" — the actual root cause — instead of
// "First name is required", which would mislead about what's missing.
const schema = z.object({
  clinic_id: z.coerce.number().int().positive('Choose a clinic'),
  first_name: z.string().trim().min(1, 'First name is required'),
  last_name: z.string().trim().min(1, 'Last name is required'),
  email: z.string().trim().email('Enter a valid email'),
  phone_number: z.string().optional(),
});

type Props = {
  open: boolean;
  clinicianId: number;
  onClose: () => void;
  onCreated?: (patientId: number) => void;
};

export const NewPatientDialog = ({ open, clinicianId, onClose, onCreated }: Props) => {
  const { t } = useTranslation();
  const clinics = useClinicianClinics(clinicianId);
  const createPatient = useCreatePatient(clinicianId);

  // Clinician-wide flags (used for App Settings visibility gates which the
  // server sets at the user / hospital level, not per clinic).
  const clinicianFlags = useAuthStore((s) => s.clinician?.flags) as Record<string, unknown> | undefined;

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [clinicId, setClinicId] = useState<string>('');
  const [externalId, setExternalId] = useState('');
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  // Default tracking (and therefore the subscription start) to tomorrow so
  // logging always begins the day after the patient is added.
  const [trackingStart, setTrackingStart] = useState<string>(addDaysIso(todayIso(), 1));
  const [patientType, setPatientType] = useState<string>('');
  // Subscription section — defaults to 'single' so the logging-period
  // fields render immediately even before the clinic picker resolves.
  // The cadence-snap effect below still overwrites this with whatever
  // the clinic's first allowed option is once a clinic is picked.
  const [cadence, setCadence] = useState<Cadence>('single');
  const [trialDays, setTrialDays] = useState<number>(3);
  const [recurringEnd, setRecurringEnd] = useState<string>('');
  // Free-form end date for the `custom` cadence. Recurring cadences pick
  // from a constrained list of cadence-multiples instead (`recurringEnd`);
  // custom is exactly the case where the clinician needs to name any date.
  const [customEnd, setCustomEnd] = useState<string>('');
  const [reportDate, setReportDate] = useState<string>('');
  // Per-patient app-flag overrides. Sent as `flags_raw` on submit; only
  // keys explicitly set by the clinician are included so the server keeps
  // clinic defaults for everything else.
  const [patientFlags, setPatientFlags] = useState<PatientFlagsState>({});
  const [error, setError] = useState<string | null>(null);

  const selectedClinic = useMemo(
    () => (clinics.data ?? []).find((c) => c.id === Number(clinicId)),
    [clinics.data, clinicId],
  );

  // Clinic-driven flags. v1 reads these from `primaryClinic?.flags` (the
  // currently-selected clinic) — not the clinician's own flag bag — so a
  // clinician at multiple clinics under different hospitals gets the right
  // form for whichever clinic they're enrolling the patient into.
  const clinicFlags = (selectedClinic?.flags ?? {}) as Record<string, unknown>;
  const hasExternalId = clinicFlags.hospital_patient_has_external_id === true;
  const customFieldDefs = useMemo(
    () => parseCustomFields(clinicFlags.hospital_patient_custom_fields_json),
    [clinicFlags.hospital_patient_custom_fields_json],
  );
  const trialLengthsFlag =
    typeof clinicFlags.clinician_available_trial_lengths === 'string'
      ? (clinicFlags.clinician_available_trial_lengths as string)
      : '3,5';
  const trialLengthOptions = useMemo(
    () =>
      trialLengthsFlag
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0 && TRIAL_DAYS_TO_NAME[n]),
    [trialLengthsFlag],
  );
  // Clinic-defined report-date offset (days past the subscription's
  // expiration). Falls back to the server's own default for the flag
  // (`clinic_default_days_until_report_date`, MNT feature_flags.py) so an
  // unresolved flag lands on the same date the server would have picked.
  const reportDateOffsetDays = useMemo(() => {
    const flag = clinicFlags.clinic_default_days_until_report_date;
    const n = Number(flag);
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_DAYS_UNTIL_REPORT;
  }, [clinicFlags.clinic_default_days_until_report_date]);

  // Minimum turnaround between the end of logging and the report date.
  // v1 gated this on the clinician's `clinician_report_24h_turnaround`
  // flag (addUser.tsx:564-567) — 1 day when RxFood turns reports around in
  // 24h, 2 otherwise.
  const turnaroundDays = clinicianFlags?.clinician_report_24h_turnaround === true ? 1 : 2;

  const cadenceOptions = useMemo(() => addPatientCadences(selectedClinic), [selectedClinic]);

  /**
   * The date the logging period ends — i.e. the `subscription_expiration_date`
   * the server will store. Single trials derive it from the trial length
   * (the server computes `tracking_start + SUBSCRIPTION_LENGTHS[name]`, so
   * this matches what it would derive anyway); recurring and custom take the
   * clinician's picked end date.
   */
  const subscriptionEnd = useMemo(() => {
    if (cadence === 'none') return '';
    if (cadence === 'single') return addDaysIso(trackingStart, trialDays);
    if (cadence === 'custom') return customEnd;
    return recurringEnd;
  }, [cadence, trackingStart, trialDays, customEnd, recurringEnd]);

  /** Earliest report date the clinic's turnaround allows. */
  const reportDateMin = useMemo(
    () => (subscriptionEnd ? addDaysIso(subscriptionEnd, turnaroundDays) : ''),
    [subscriptionEnd, turnaroundDays],
  );

  // Don't pre-pick a clinic — making the clinician choose deliberately
  // prevents accidentally registering a patient against the first clinic
  // in the list when they meant another. v1 auto-picked the first
  // clinic; V2 makes it explicit per UX feedback. The only thing this
  // effect still does is clear stale errors on re-open.
  useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open]);

  // Snap cadence to first allowed option whenever the clinic changes.
  useEffect(() => {
    if (!open) return;
    if (cadenceOptions.length === 0) return;
    if (!cadenceOptions.includes(cadence)) setCadence(cadenceOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cadenceOptions, open]);

  // For recurring cadence, default the end date to the first valid option
  // when the cadence or start date changes.
  useEffect(() => {
    if (!open) return;
    if (cadence === 'single' || cadence === 'none' || cadence === 'custom') return;
    const opts = recurringEndOptions(trackingStart, cadence);
    setRecurringEnd((cur) => (opts.includes(cur) ? cur : opts[0] ?? ''));
  }, [cadence, trackingStart, open]);

  // Snap trial length to the first allowed option whenever the clinic-driven
  // list changes (clinician switches clinics or first opens the dialog).
  useEffect(() => {
    if (!open) return;
    if (trialLengthOptions.length === 0) return;
    if (!trialLengthOptions.includes(trialDays)) {
      setTrialDays(trialLengthOptions[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialLengthOptions, open]);

  // For custom cadence, seed the end date the first time it's needed. The
  // clinician is expected to change it — this only keeps the input from
  // opening empty. Four weeks is a neutral starting point: it's the longest
  // of the recurring cadences, and it's always after `trackingStart`, which
  // the submit check requires.
  useEffect(() => {
    if (!open) return;
    if (cadence !== 'custom') return;
    setCustomEnd((cur) => (cur && cur > trackingStart ? cur : addDaysIso(trackingStart, CUSTOM_END_SEED_DAYS)));
  }, [cadence, trackingStart, open]);

  // Default the report date to (end of logging period +
  // `clinic_default_days_until_report_date` days) — the same date the server
  // derives when `first_report_date` is omitted (MNT patient.py:1736-1745).
  // Applies to the two cadences that expose a report-date input; recurring
  // periods leave it to the server, matching v1.
  useEffect(() => {
    if (!open) return;
    if (cadence !== 'single' && cadence !== 'custom') return;
    if (!subscriptionEnd) return;
    setReportDate(addDaysIso(subscriptionEnd, reportDateOffsetDays));
  }, [cadence, subscriptionEnd, reportDateOffsetDays, open]);

  // Reset the form when it closes so the next open is fresh.
  useEffect(() => {
    if (open) return;
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setClinicId('');
    setExternalId('');
    setCustomFields({});
    setTrackingStart(addDaysIso(todayIso(), 1));
    setPatientType('');
    setCadence('single');
    setTrialDays(trialLengthOptions[0] ?? 3);
    setRecurringEnd('');
    setCustomEnd('');
    setReportDate('');
    setPatientFlags({});
    setError(null);
    // Intentionally only triggered by `open`. Adding `trialLengthOptions`
    // would reset every field whenever the options list changed (e.g. clinic
    // switch mid-form), wiping the user's in-progress entries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const recurringEnds = useMemo(
    () => (cadence !== 'single' && cadence !== 'none' && cadence !== 'custom'
      ? recurringEndOptions(trackingStart, cadence)
      : []),
    [cadence, trackingStart],
  );

  const buildPayload = (validatedBase: { first_name: string; last_name: string; email: string; phone_number?: string; clinic_id: number }): CreatePatientInput => {
    const payload: CreatePatientInput = {
      ...validatedBase,
      phone_number: validatedBase.phone_number || null,
    };
    if (hasExternalId && externalId.trim()) {
      payload.hospital_patient_external_id = externalId.trim();
    }
    if (customFieldDefs.length > 0) {
      const entries: Record<string, string> = {};
      for (const def of customFieldDefs) {
        const v = customFields[def.field];
        if (v && v.trim()) entries[def.field] = v.trim();
      }
      if (Object.keys(entries).length > 0) {
        payload.hospital_patient_custom_fields = entries;
      }
    }
    if (patientType) {
      payload.patient_type = patientType as (typeof PATIENT_TYPES)[number];
    }
    // Patient flag overrides — only include the keys explicitly set by the
    // clinician. Keys absent here keep the clinic's default value server-side.
    if (Object.keys(patientFlags).length > 0) {
      const raw: Record<string, string | boolean | number | null> = {};
      for (const [k, v] of Object.entries(patientFlags)) {
        if (v === undefined) continue;
        raw[k] = v as string | boolean | number;
      }
      if (Object.keys(raw).length > 0) payload.flags_raw = raw;
    }

    // Subscription block: shape varies by cadence, mirroring v1's addUser.
    if (cadence === 'none') {
      payload.subscription_name = 'none';
      return payload;
    }

    payload.tracking_start_date = trackingStart || undefined;

    if (cadence === 'single') {
      payload.subscription_type = 'single';
      payload.subscription_name = TRIAL_DAYS_TO_NAME[trialDays] ?? 'three-day';
      // Send the expiration explicitly even though the server can derive it
      // from `subscription_name`. Its derivation runs through a clinic-flag
      // fallback that can discard the requested name (FOOD-1070); sending
      // the date makes the length the clinician picked authoritative.
      if (subscriptionEnd) payload.subscription_expiration_date = subscriptionEnd;
      if (reportDate) payload.first_report_date = reportDate;
      return payload;
    }

    if (cadence === 'custom') {
      payload.subscription_type = 'custom';
      payload.subscription_name = 'custom';
      // A custom period has no implied length, so the end date is the only
      // thing that defines it — the server rejects `custom` without one.
      if (customEnd) payload.subscription_expiration_date = customEnd;
      if (reportDate) payload.first_report_date = reportDate;
      return payload;
    }

    // Recurring: use the cadence-derived length + the user-picked end date.
    const days = RECURRING_DAYS[cadence] ?? 7;
    payload.subscription_type = cadence;
    payload.subscription_name = TRIAL_DAYS_TO_NAME[days] ?? 'seven-day';
    if (recurringEnd) payload.subscription_expiration_date = recurringEnd;
    return payload;
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = schema.safeParse({
      first_name: firstName,
      last_name: lastName,
      email,
      phone_number: phone || undefined,
      clinic_id: clinicId,
    });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t('Please review the form.'));
      return;
    }
    // Date rules the zod schema can't express (they depend on cadence and on
    // clinic/clinician flags). The form is `noValidate`, so the `min`
    // attributes on the date inputs are hints only — enforce them here.
    if (cadence === 'custom' && (!customEnd || customEnd <= trackingStart)) {
      setError(t('Choose an end date after the tracking start date.'));
      return;
    }
    if ((cadence === 'single' || cadence === 'custom') && reportDate && reportDate < reportDateMin) {
      // Phrased as the earliest allowed date rather than "at least N days
      // after", which reads as "at least 1 days" on a 24h-turnaround clinic.
      setError(t('The report date must be {{date}} or later.', { date: reportDateMin }));
      return;
    }
    const payload = buildPayload(parsed.data);
    try {
      const result = await createPatient.mutateAsync(payload);
      toast({ title: t('Patient added'), description: `${parsed.data.first_name} ${parsed.data.last_name}` });
      track(ANALYTICS_EVENTS.PATIENT_ADDED, {
        // Presence-of-fields gives useful signal without leaking PII.
        // patient_id keys are stripped by sanitizeSegmentation anyway,
        // but we explicitly avoid sending names/email here too — only
        // boolean "had a value" flags pass through.
        has_email: !!parsed.data.email,
        has_phone: !!parsed.data.phone_number,
      });
      onClose();
      const newId = (result as { patient_id?: number; id?: number }).patient_id ?? (result as { id?: number }).id;
      if (newId && onCreated) onCreated(newId);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('Could not create patient. Check the form and try again.');
      setError(msg);
      toast({ title: t('Could not create patient'), variant: 'destructive', description: msg });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[min(96vw,1100px)] max-w-[96vw] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Add patient')}</DialogTitle>
          <DialogDescription>
            {t('They will receive an invitation email at the address you provide.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          {/* Two-column layout on lg+ — primary fields on the left, App
              Settings + phone preview on the right. The right column is
              conditionally rendered only when the App Settings panel
              actually has any rows to show; otherwise the left column
              expands full-width so the dialog doesn't look unbalanced. */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
            {/* ===================== LEFT COLUMN ===================== */}
            <div className="space-y-4">
              {/* --- Clinic (searchable combobox) ---
                  Pulled to the top because the selected clinic gates the
                  rest of the form: per-clinic External ID / custom-fields
                  toggle off the clinic flags, and the cadence dropdown
                  pulls its choices from the clinic. Picking the clinic
                  first means the user fills in the right fields. Also
                  the initial autoFocus target — the dialog opens with
                  the trigger focused so a keyboard user lands here. */}
              <div className="space-y-1">
                <Label>{t('Clinic')}</Label>
                {clinics.isLoading ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <ClinicCombobox
                    clinics={clinics.data ?? []}
                    value={clinicId ? Number(clinicId) : null}
                    onChange={(id) => setClinicId(String(id))}
                    placeholder={t('Select a clinic')}
                    autoFocus
                  />
                )}
              </div>

              {/* --- Identity --- */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="np-first">{t('First name')}</Label>
                  <Input id="np-first" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="np-last">{t('Last name')}</Label>
                  <Input id="np-last" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="np-email">{t('Email')}</Label>
                <Input id="np-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label htmlFor="np-phone">{t('Phone (optional)')}</Label>
                <Input id="np-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              {/* --- External ID + custom fields (per-clinic, conditional)
                   Per v1 (clinician/src/components/addUser.tsx:1436), both
                   are gated by the SELECTED CLINIC's flags — not the
                   clinician's. Multi-hospital clinicians get the right form
                   for whichever clinic they pick. --- */}
              {hasExternalId && (
                <div className="space-y-1">
                  <Label htmlFor="np-external-id">{t('Patient ID (optional)')}</Label>
                  <Input
                    id="np-external-id"
                    value={externalId}
                    onChange={(e) => setExternalId(e.target.value)}
                    placeholder={t('e.g. MRN-12345')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t("Hospital's record identifier for this patient.")}
                  </p>
                </div>
              )}
              {customFieldDefs.length > 0 && (
                <div className="space-y-3 rounded-md border bg-muted/20 p-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {t('Hospital fields')}
                  </p>
                  {customFieldDefs.map((def) => (
                    <div key={def.field} className="space-y-1">
                      <Label htmlFor={`np-cf-${def.field}`}>{def.label}</Label>
                      {def.values ? (
                        <select
                          id={`np-cf-${def.field}`}
                          value={customFields[def.field] ?? ''}
                          onChange={(e) =>
                            setCustomFields((prev) => ({ ...prev, [def.field]: e.target.value }))
                          }
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">{t('— none —')}</option>
                          {Object.entries(def.values).map(([key, opt]) => (
                            <option key={key} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <Input
                          id={`np-cf-${def.field}`}
                          value={customFields[def.field] ?? ''}
                          onChange={(e) =>
                            setCustomFields((prev) => ({ ...prev, [def.field]: e.target.value }))
                          }
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* --- Subscription / first logging period (moved up — matches
                   v1's order: identity → clinic → trial setup → app settings) --- */}
          <div className="space-y-3 rounded-md border bg-muted/20 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('Initial logging period')}
            </p>
            <div className="space-y-1">
              <Label htmlFor="np-cadence">{t('Cadence')}</Label>
              <select
                id="np-cadence"
                value={cadence}
                onChange={(e) => setCadence(e.target.value as Cadence)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {cadenceOptions.map((c) => (
                  <option key={c} value={c}>
                    {t(CADENCE_LABELS[c])}
                  </option>
                ))}
              </select>
            </div>

            {cadence !== 'none' && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="np-tracking">{t('Tracking start date')}</Label>
                  <Input
                    id="np-tracking"
                    type="date"
                    value={trackingStart}
                    onChange={(e) => setTrackingStart(e.target.value)}
                  />
                </div>

                {cadence === 'single' && (
                  <div className="space-y-1">
                    <Label htmlFor="np-trial-days">{t('Logging Period (days)')}</Label>
                    <select
                      id="np-trial-days"
                      value={String(trialDays)}
                      onChange={(e) => setTrialDays(Number(e.target.value))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {trialLengthOptions.map((d) => (
                        <option key={d} value={d}>
                          {d} {t('day trial')}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Custom cadence: the end date is free-form — that's what
                    makes it custom. Recurring cadences constrain it to
                    cadence-multiples via the <select> below instead. */}
                {cadence === 'custom' && (
                  <div className="space-y-1">
                    <Label htmlFor="np-custom-end">{t('End date')}</Label>
                    <Input
                      id="np-custom-end"
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      min={addDaysIso(trackingStart, 1)}
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('The logging period runs from the start date up to this date.')}
                    </p>
                  </div>
                )}

                {/* Report date — shown for the cadences where the clinician
                    names the end of logging. Recurring periods leave it to
                    the server to derive from the end date (v1 parity). */}
                {(cadence === 'single' || cadence === 'custom') && (
                  <div className="space-y-1">
                    <Label htmlFor="np-report-date">{t('Report date')}</Label>
                    <Input
                      id="np-report-date"
                      type="date"
                      value={reportDate}
                      onChange={(e) => setReportDate(e.target.value)}
                      min={reportDateMin || undefined}
                    />
                    {subscriptionEnd && (
                      <p className="text-xs text-muted-foreground">
                        {t('Logging ends {{date}}.', { date: subscriptionEnd })}
                      </p>
                    )}
                  </div>
                )}

                {cadence !== 'single' && cadence !== 'custom' && (
                  <div className="space-y-1">
                    <Label htmlFor="np-recurring-end">{t('End date')}</Label>
                    <select
                      id="np-recurring-end"
                      value={recurringEnd}
                      onChange={(e) => setRecurringEnd(e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {recurringEnds.length === 0 && <option value="">—</option>}
                      {recurringEnds.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-muted-foreground">
                      {t('Limited to cadence-multiples within 6 months.')}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
            </div>

            {/* ===================== RIGHT COLUMN =====================
                Per-patient app-flag overrides + phone preview. The section
                hides itself when no overridable flags are visible to this
                clinic, in which case the left column gets the full width. */}
            <div>
              <PatientFlagsSection
                clinic={selectedClinic}
                clinicianFlags={clinicianFlags}
                value={patientFlags}
                onChange={setPatientFlags}
              />
            </div>
          </div>

          {/*
           * `patient_type` is on its way out per the team. Hidden in the
           * form (existing values still round-trip through PATCH on edit).
           */}
          {/* eslint-disable-next-line no-constant-binary-expression */}
          {false && (
            <div className="space-y-1">
              <Label htmlFor="np-type">{t('Patient type (optional)')}</Label>
              <select
                id="np-type"
                value={patientType}
                onChange={(e) => setPatientType(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">{t('— none —')}</option>
                {PATIENT_TYPES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          )}

          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={createPatient.isPending}>
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={createPatient.isPending}>
              {createPatient.isPending ? t('Creating…') : t('Create patient')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
